import React from 'react';
import {
  Box,
  CardContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { ChevronLeft, ChevronRight, Tune } from '@mui/icons-material';
import {
  ACCOUNT_TREND_GRANULARITIES,
  ACCOUNT_TREND_METRICS,
  metricLabel,
  type AccountTrendGranularity,
  type AccountTrendMetric
} from '../../types/accountTrend';
import DashboardControlsStickyShell from '../dashboard/DashboardControlsStickyShell';
import { useAccountDashboard } from './AccountDashboardContext';

export default function AccountDashboardControls() {
  const {
    accessGranted,
    metric,
    granularity,
    focus,
    trendLoading,
    setMetric,
    setGranularity,
    goPrev,
    goNext
  } = useAccountDashboard();

  if (!accessGranted) {
    return null;
  }

  const navDisabled = granularity === 'all' || (trendLoading && !focus);
  const focusLabel = focus?.label ?? '…';

  return (
    <DashboardControlsStickyShell>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tune color="primary" fontSize="small" />
            <Typography variant="subtitle1">Dashboard view</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={metric}
              onChange={(_, value: AccountTrendMetric | null) => {
                if (value) setMetric(value);
              }}
            >
              {ACCOUNT_TREND_METRICS.map((m) => (
                <ToggleButton key={m} value={m}>
                  {metricLabel(m)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, value: AccountTrendGranularity | null) => {
                if (value) setGranularity(value);
              }}
            >
              {ACCOUNT_TREND_GRANULARITIES.map((g) => (
                <ToggleButton key={g} value={g}>
                  {g}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 160, justifyContent: 'center' }}>
              <Tooltip title="Previous period">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Previous period"
                    onClick={goPrev}
                    disabled={navDisabled || !focus?.canGoPrev}
                  >
                    <ChevronLeft />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center', fontWeight: 500 }}>
                {focusLabel}
              </Typography>
              <Tooltip title="Next period">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Next period"
                    onClick={goNext}
                    disabled={navDisabled || !focus?.canGoNext}
                  >
                    <ChevronRight />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </DashboardControlsStickyShell>
  );
}
